// src/components/Auth/ProtectedRoute.tsx
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { CircularProgress, Box } from '@mui/material';
import { Role, getPrimaryRole, hasRole, hasAnyRole } from '../../services/authApi';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: Role;
  allowedRoles?: Role[];
}

export function ProtectedRoute({ children, requiredRole, allowedRoles }: ProtectedRouteProps) {
  const { user, loading, isAuthenticated } = useAuth();

  // Show loading state while checking authentication
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // Redirect to login if not authenticated
  // After login, user will be redirected to /main (or stored path if it's a specific page)
  // Only redirect if we're sure we're not authenticated (not loading and no user)
  // Don't auto-redirect if we just logged out (give backend time to clear session)
  if (!loading && (!isAuthenticated || !user)) {
    const justLoggedOut = sessionStorage.getItem('justLoggedOut') === 'true';
    if (justLoggedOut) {
      // Just logged out - show a message or stay on page, don't auto-redirect
      // The user can manually navigate to login if needed
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" flexDirection="column" gap={2}>
          <Box>You have been logged out.</Box>
          <Box component="a" href="/login" sx={{ color: 'primary.main', textDecoration: 'underline' }}>
            Click here to log in
          </Box>
        </Box>
      );
    }
    
    // Store the intended destination to redirect after login
    // Only store if it's not the root path (/) - root should always go to /main
    const currentPath = window.location.pathname + window.location.search; // Include query params
    if (currentPath !== '/main' && currentPath !== '/') {
      sessionStorage.setItem('redirectAfterLogin', currentPath);
    }
    // Redirect to login page
    return <Navigate to="/login" replace />;
  }

  // Check role requirements (user is guaranteed to be non-null at this point)
  if (user) {
    // Check if user has no role assigned
    const primaryRole = getPrimaryRole(user);
    if (!primaryRole || user.roles.length === 0) {
      return <Navigate to="/no-role" replace />;
    }

    // Check required role (exact match - user must have this specific role)
    if (requiredRole && !hasRole(user, requiredRole)) {
      return <Navigate to="/unauthorized" replace />;
    }

    // Check allowed roles (user must have one of the allowed roles)
    if (allowedRoles && !hasAnyRole(user, allowedRoles)) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return <>{children}</>;
}

